/**
 * src/components/reversedVoucher.js
 *
 * "RV" — vouchered, then reversed. Marks the POs on the third-party tabs whose
 * vendor invoice was vouchered and then reversed in full, leaving no posted cost
 * and nothing open, so the row shows blank (see helpers/reversedPos.js).
 *
 *   useReversedPos()            – the job's flagged POs, computed once per data load
 *   useReversedRow(node)        – flagged POs under a tree node (cached per node)
 *   <ReversedTag entries node>  – the tiny "RV" tag a row shows, hover for detail
 *   <ReversedVoucherLegend tree> – toolbar legend listing the tab's flagged POs
 */
import React, { useMemo, useState } from 'react';
import { View, Text, Pressable, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { jobCostDetailAtom, jobOpenCommitmentsAtom } from '../store/company';
import { fNum } from '../db/company';
import { findReversedPos, reversedUnder, reversedInTree, lineKey } from '../helpers/reversedPos';

// Every row reads the same result, so compute it once per cost/OC load rather
// than once per row.
let flaggedCache = { ch: undefined, oc: undefined, flagged: new Map() };
export const useReversedPos = () => {
    const [costRows] = useAtom(jobCostDetailAtom);
    const [ocRows] = useAtom(jobOpenCommitmentsAtom);
    if (flaggedCache.ch !== costRows || flaggedCache.oc !== ocRows) {
        flaggedCache = { ch: costRows, oc: ocRows, flagged: findReversedPos(costRows, ocRows) };
    }
    return flaggedCache.flagged;
};

// Grouping rows walk their subtree, so cache by tree node (rebuilt trees are
// new objects, which drops stale entries).
const nodeCache = new WeakMap();
export const useReversedRow = (node, skip) => {
    const flagged = useReversedPos();
    if (skip || !node || !flagged.size) return [];
    const hit = nodeCache.get(node);
    if (hit && hit.flagged === flagged) return hit.entries;
    const entries = reversedUnder(node, flagged);
    nodeCache.set(node, { flagged, entries });
    return entries;
};

const mdy = (day) => {
    const [y, m, d] = String(day || '').split('-');
    return y && m && d ? `${+m}/${+d}/${y}` : '';
};
const money = (v) => '$' + fNum(v);

const tagBox = {
    backgroundColor: '#334155', borderRadius: 3, height: 14,
    paddingLeft: 4, paddingRight: 4, justifyContent: 'center', alignItems: 'center', flexShrink: 0,
};
const tagText = { fontSize: 9, fontWeight: '700', color: '#ffffff', letterSpacing: 0.4, lineHeight: '14px' };

const Tag = ({ text, title, style }) => (
    <View title={title} style={[tagBox, style]}>
        <Text style={tagText}>{text}</Text>
    </View>
);

const rowTooltip = (node, entries) => {
    const path = node?.data?.path;
    if (path === 'Line_ID') {
        const e = entries[0];
        const ln = e.lines.get(lineKey(node.data.key));
        return `Vouchered, then reversed — line ${node.data.key}: ${money(ln?.posted || 0)} vouchered${ln?.last ? ' and reversed by ' + mdy(ln.last) : ''}.\n`
            + `PO ${e.po} nets to $0 with nothing open, so this line shows blank. If the work is real, carry it in ETC until AP re-posts the invoice.`;
    }
    if (path === 'PO_Number') {
        const e = entries[0];
        return `Vouchered, then reversed — ${money(e.posted)} vouchered on this PO${e.last ? ' and reversed by ' + mdy(e.last) : ''}.\n`
            + `No posted cost or open commitment is left, so the PO shows blank. If the work is real, carry it in ETC until AP re-posts the invoice.`;
    }
    const total = entries.reduce((s, e) => s + e.posted, 0);
    const list = entries.slice(0, 8).map((e) => `PO ${e.po}  ${money(e.posted)}${e.last ? '  ' + mdy(e.last) : ''}`).join('\n');
    const lead = entries.length === 1
        ? `1 PO below was vouchered, then reversed in full (${money(total)}). It shows blank: no posted cost, nothing open.`
        : `${entries.length} POs below were vouchered, then reversed in full (${money(total)}). They show blank: no posted cost, nothing open.`;
    return `${lead}\n${list}${entries.length > 8 ? `\n…and ${entries.length - 8} more` : ''}`;
};

// The tag a tree row shows: "RV" on a reversed PO or line, "RV n" on a grouping
// row with n reversed POs beneath it.
export const ReversedTag = ({ entries, node, style }) => {
    if (!entries?.length) return null;
    const path = node?.data?.path;
    const single = path === 'PO_Number' || path === 'Line_ID' || entries.length === 1;
    return <Tag text={single ? 'RV' : `RV ${entries.length}`} title={rowTooltip(node, entries)} style={style} />;
};

// Toolbar legend: how many POs on this tab are reversed, with a list on click.
export const ReversedVoucherLegend = ({ tree }) => {
    const flagged = useReversedPos();
    const list = useMemo(() => reversedInTree(tree, flagged), [tree, flagged]);
    const [open, setOpen] = useState(false);
    if (!list.length) return null;
    const total = list.reduce((s, e) => s + e.posted, 0);
    return (
        <View style={{ position: 'relative' }}>
            <Pressable
                onPress={() => setOpen(!open)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 6, cursor: 'pointer', height: 26, paddingLeft: 6, paddingRight: 6, borderRadius: 4, border: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}
            >
                <Tag text="RV" />
                <Text style={{ fontSize: 11, fontWeight: '600', color: '#334155' }}>
                    {list.length} PO{list.length === 1 ? '' : 's'} vouchered, then reversed · {money(total)}
                </Text>
                <Icon3 name={open ? 'MdExpandLess' : 'MdExpandMore'} size={14} color="#64748b" />
            </Pressable>
            {open && (
                <View style={{ position: 'absolute', top: 30, left: 0, zIndex: 150, width: 560, maxHeight: 360, overflowY: 'auto', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 6, boxShadow: '0 8px 24px rgba(15,23,42,0.15)' }}>
                    <View style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 12, paddingRight: 12, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                        <Text style={{ fontSize: 12, fontWeight: '700', color: '#0f172a' }}>RV — vouchered, then reversed</Text>
                        <Text style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                            The vendor invoice posted and was reversed in full. No cost and nothing open is left, so these POs show blank. If the work is real, carry it in ETC until AP re-posts the invoice.
                        </Text>
                    </View>
                    <View style={{ flexDirection: 'row', paddingTop: 6, paddingBottom: 4, paddingLeft: 12, paddingRight: 12 }}>
                        {[['Vendor', 220, 'left'], ['PO', 80, 'left'], ['Lines', 80, 'left'], ['Vouchered', 90, 'right'], ['Reversed by', 66, 'right']].map(([h, w, a]) => (
                            <Text key={h} style={{ width: w, fontSize: 10, fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: a }}>{h}</Text>
                        ))}
                    </View>
                    {list.map((e) => (
                        <View key={e.po} style={{ flexDirection: 'row', alignItems: 'center', height: 24, paddingLeft: 12, paddingRight: 12, borderTop: '1px solid #f1f5f9' }}>
                            <Text numberOfLines={1} style={{ width: 220, fontSize: 12, color: '#1e293b', paddingRight: 8 }}>{e.vendor}</Text>
                            <Text style={{ width: 80, fontSize: 12, color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>{e.po}</Text>
                            <Text numberOfLines={1} style={{ width: 80, fontSize: 12, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
                                {[...e.lines.keys()].filter(Boolean).sort((a, b) => a - b).join(', ')}
                            </Text>
                            <Text style={{ width: 90, fontSize: 12, color: '#1e293b', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(e.posted)}</Text>
                            <Text style={{ width: 66, fontSize: 12, color: '#475569', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{mdy(e.last)}</Text>
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
};
